/**
 * mandate.ts — F2 shared wire types for the Mahnwesen reminder dispatch (migration 0167).
 *
 * The GET /vorgaenge/:id/mahnungen read surface + the per-stage status vocab. snake_case wire
 * fields, no PII (no addresses, names, or mail bodies — only ids, counts, codes and statuses).
 *
 * [Source: packages/db/migrations/0167_vorgang_mahnung.sql] (the vorgang_mahnung table)
 */

export const VORGANG_MAHNUNG_STATUS = {
  PENDING: 'pending',
  SENT: 'sent',
  FAILED: 'failed',
  NO_RECIPIENT: 'no_recipient',
  NO_SENDER: 'no_sender',
  MANUAL: 'manual',
  STALE: 'stale',
} as const;
export type VorgangMahnungStatus = (typeof VORGANG_MAHNUNG_STATUS)[keyof typeof VORGANG_MAHNUNG_STATUS];

/** GET /vorgaenge/:id/mahnungen row. snake_case wire fields, no PII (no addresses). */
export interface VorgangMahnungDto {
  id: string;
  vorgang_id: string;
  stage: number;
  channel: 'email' | 'post' | 'portal';
  status: VorgangMahnungStatus;
  actor_kind: 'system' | 'human';
  communication_id: string | null;
  portal_delivery_id: string | null;
  error_code: string | null;
  attempted_at: string;
  settled_at: string | null;
}

/** Outcome of one stage fire (service return). */
export type MahnungOutcome = VorgangMahnungStatus | 'escalated' | 'noop';

// ── P4-S1: Mandatsvorgang wire types (prepare/signer-proposal/release/badges) ──
//
// camelCase wire fields (the E6 wizard DTO convention; NOT the snake_case F2 reader above).
// No PII in the badge/proposal rows beyond the signer names that the covering document shows by
// design (a signature block is a name on a letter); audit/task meta carry ids + codes only (rule #6).

/** How a signer got onto the mandate. */
export const MANDATE_SIGNER_SOURCE_VALUES = ['self', 'register', 'contact', 'manual'] as const;
export type MandateSignerSourceValue = (typeof MANDATE_SIGNER_SOURCE_VALUES)[number];

/** The most signers one mandate may carry (DTO validation and the server default list). */
export const MAX_MANDATE_SIGNERS = 10;

/** "Vorname Nachname" for a signer, empty parts dropped. */
export function signerDisplayName(firstName: string | null, lastName: string | null): string {
  return [firstName, lastName].map((p) => (p ?? '').trim()).filter((p) => p !== '').join(' ');
}

/** One Unterzeichnende/r, as proposed and as persisted (step 3). */
export interface MandateSignerInput {
  source: MandateSignerSourceValue;
  personId?: string | null;
  firstName: string | null;
  lastName: string | null;
  roleText?: string | null;
  /** signatory-parser SIGNATURE vocab (einzel, kollektiv_2, ...); null for self/manual. */
  signatureKind?: string | null;
}

/** A persisted signer echoed back in the prepare response. */
export interface MandateSignerDto extends MandateSignerInput {
  id: string;
  sortOrder: number;
}

/** Whether a signer already has a reference signature (a P4 one is reused for the Brokermandat). */
export type SignerReferenceStatus = 'bestaetigt' | 'provisorisch' | 'keine';

/** A proposed signer; the wizard's proposal also says whether a reference signature exists. */
export type MandateSignerProposalRow = MandateSignerInput & { referenceStatus?: SignerReferenceStatus };

/** GET /mandates/signer-proposal (concept 7.1 step 3, Kap. 6.1). */
export interface MandateSignerProposalDto {
  subjectType: 'person' | 'firma';
  subjectId: string;
  /** person -> nicht_zutreffend; firma with an effective set -> verifiziert; firma without -> nicht_verifiziert. */
  registerStatus: 'verifiziert' | 'nicht_verifiziert' | 'nicht_zutreffend';
  registerSource: 'excerpt_pdf' | 'zefix_replay' | null;
  registerFetchedAt: string | null;
  proposal: MandateSignerProposalRow[];
}

/**
 * The covering text's process per mandate kind (Stefan 17.1): the Analysemandat is covered by
 * P-01.S6, the Brokermandat by P-02.S6.
 */
export const MANDATE_KIND_TO_COVERING_PROCESS = { analyse: 'P-01', broker: 'P-02' } as const;
export type MandateCoveringProcess =
  (typeof MANDATE_KIND_TO_COVERING_PROCESS)[keyof typeof MANDATE_KIND_TO_COVERING_PROCESS];

/** The Handlungsbaustein appended to the covering text per mandate kind (Stefan 17.2/17.3). */
export const HANDLUNGSBAUSTEIN_REF_BY_KIND = { analyse: 'P-04.S5', broker: 'P-05.S8' } as const;

/**
 * The portal notification the broker reviewed and edited in the wizard, stored with the release
 * and used by the later portal delivery (PR #66 decision 3). Portal mandates only.
 */
export interface MandateReleaseNotification {
  subject: string;
  body: string;
  /** The form of address the text was composed in. */
  anrede: 'sie' | 'du';
}

/** POST /mandates/:id/release body. Empty for a post mandate. */
export interface MandateReleaseInput {
  notification?: MandateReleaseNotification;
}

/** POST /mandates/:id/release response (Einzelfreigabe, concept step 6). */
export interface MandateReleaseResponseDto {
  mandateId: string;
  mandateStatus: 'released';
  releaseMode: 'manual';
  releasedAt: string;
  processNumber: string | null;
  /** true when the release stored an edited portal notification. */
  notificationStored: boolean;
  /** A post Brokermandat: the portal Begleitbrief is ready to print with the mandate. */
  portalBrief?: boolean;
}

/** One Mandatskachel (concept 7.6, 10.5). */
export interface MandateBadgeDto {
  kind: 'analyse' | 'broker';
  state: 'nicht_erteilt' | 'in_verarbeitung' | 'unterzeichnet' | 'gekuendigt' | 'beendet';
  label: string;
  mandateId: string | null;
  vorgangId: string | null;
  processNumber: string | null;
  mahnStage: number;
  at: string | null;
  actorLabel: string | null;
  /**
   * A released post or portal mandate still waiting for its delivery (the latest of this kind).
   * The tile then offers Zustellen and Freigabe zurückziehen. null (or absent) otherwise.
   */
  pendingDelivery?: MandatePendingDeliveryDto | null;
  /**
   * A gekündigt or beendet mandate (P6): the inclusive Enddatum, when the Freigabe took effect and who
   * approved it. Absent otherwise.
   */
  termination?: { ends_on: string | null; effective_at: string | null; approved_by_app_user_id: string | null } | null;
}

/** A released (freigegeben) post or portal mandate that has not been delivered yet. */
export interface MandatePendingDeliveryDto {
  mandateId: string;
  /** email: a released email mandate whose send did not complete; it can only be withdrawn here. */
  channel: 'post' | 'portal' | 'email';
  processNumber: string | null;
  releasedAt: string | null;
  /** A post Brokermandat: the portal Begleitbrief (activation link + code) is ready to print with it. */
  portalBrief?: boolean;
}

/**
 * GET /mandates/:id/delivery - what a Zustellen of a released mandate would send. For portal: the
 * recipient the covering notification goes to and the notification stored with the release (null
 * when none was stored). For post: no recipient and no text, the document is printed.
 */
export interface MandateDeliveryPreviewDto {
  mandateId: string;
  kind: 'analyse' | 'broker';
  channel: 'post' | 'portal';
  processNumber: string | null;
  to: string[];
  subject: string | null;
  body: string | null;
}

/** GET /customers/:subjectType/:subjectId/mandate-badges. */
export interface MandateBadgesResponseDto {
  analyse: MandateBadgeDto;
  broker: MandateBadgeDto;
}

/** Wire aliases used by the web client. */
export type MandateSignerWire = MandateSignerInput & { id?: string; sortOrder?: number };
export type MandateSignerProposalWire = MandateSignerProposalDto;
export type MandateReleaseResultWire = MandateReleaseResponseDto;
export type MandateBadgeWire = MandateBadgeDto;
export type MandateBadgesWire = MandateBadgesResponseDto;
export type MandatePendingDeliveryWire = MandatePendingDeliveryDto;
export type MandateDeliveryPreviewWire = MandateDeliveryPreviewDto;

// ════════════════════════════════════════════════════════════════════════════
// P4-S2: Zustellung & Mahnwesen (parameter set, Versandfenster, dunning events, tile)
// ════════════════════════════════════════════════════════════════════════════

/** The Mahnschema scope key per mandate kind (mahnwesen_config.product). */
export const MAHNWESEN_PROCESS_KEY = { P04: 'P-04', P05: 'P-05' } as const;
export type MahnwesenProcessKeyValue = (typeof MAHNWESEN_PROCESS_KEY)[keyof typeof MAHNWESEN_PROCESS_KEY];

export const MAHNWESEN_TZ = 'Europe/Zurich' as const;

/** 17.5 Bandbreiten. Validated in NestJS (rule #2). */
export const MAHNWESEN_FRIST_RANGE = { min: 3, max: 30 } as const;
export const MAHNWESEN_ESCALATION_RANGE = { min: 3, max: 30 } as const;
export const MAHNWESEN_TEAMLEAD_RANGE = { min: 1, max: 30 } as const;
export const MAHNWESEN_EXTEND_RANGE = { min: 1, max: 60 } as const;
/**
 * Reminder stages a mandate Mahnschema may hold (17.5: two or three reminders). The platform wizard
 * and its PATCH enforce it, so the Administration page, which knows only 2 or 3, always shows the
 * row as stored.
 */
export const MANDATE_MAHNWESEN_STAGE_COUNT = { min: 2, max: 3 } as const;

/** GET/PUT /admin/mahnwesen/:processKey — the P4 Mahnwesen parameter set (Kap. 17.5). */
export interface MahnwesenProcessConfigDto {
  process_key: 'P-04' | 'P-05';
  reminder_count: 2 | 3;
  /** ABSOLUTE days after Zustelldatum, strictly ascending, length = reminder_count. */
  frist_days: number[];
  escalation_offset_days: number;
  teamlead_after_days: number;
  stage2_secondary_channel: boolean;
  send_window_start_hour: number;
  send_window_end_hour: number;
  send_weekdays_only: boolean;
  /** true = no tenant row yet; the values are the in-code seed (the first PUT creates the row). */
  used_default: boolean;
  updated_at: string | null;
  /** The day (after Zustelldatum) a third reminder gets when it is switched on (17.4: optional T+21). */
  optional_third_frist_day: number;
}

export type PutMahnwesenProcessConfigBody = Omit<
  MahnwesenProcessConfigDto,
  'process_key' | 'used_default' | 'updated_at' | 'optional_third_frist_day'
>;

/** The process keys the Mahnwesen admin surface accepts (GET/PUT /admin/mahnwesen/:processKey). */
export const MAHNWESEN_ADMIN_PROCESS_KEYS: readonly MahnwesenProcessKeyValue[] = [MAHNWESEN_PROCESS_KEY.P04, MAHNWESEN_PROCESS_KEY.P05];

/** Stefan's optional third reminder (P4 17.5, P5 17.4): T+21. */
export const MAHNWESEN_OPTIONAL_THIRD_FRIST_DAY = 21;

/** Stefan's P-04 seeds (17.5 table): 2 reminders at T+7 / T+14, escalation +7, Team Lead +5. */
export const P04_MAHNWESEN_DEFAULTS: PutMahnwesenProcessConfigBody = {
  reminder_count: 2,
  frist_days: [7, 14],
  escalation_offset_days: 7,
  teamlead_after_days: 5,
  stage2_secondary_channel: false,
  send_window_start_hour: 8,
  send_window_end_hour: 18,
  send_weekdays_only: true,
};

/**
 * Stefan's P-05 seeds (P5 17.4): 2 reminders at T+7 / T+14 (a third at T+21 when switched on),
 * Eskalationsfrist 7 days after the last reminder (band 3-30), Team Lead +5, the same Versandfenster.
 */
export const P05_MAHNWESEN_DEFAULTS: PutMahnwesenProcessConfigBody = {
  reminder_count: 2,
  frist_days: [7, 14],
  escalation_offset_days: 7,
  teamlead_after_days: 5,
  stage2_secondary_channel: false,
  send_window_start_hour: 8,
  send_window_end_hour: 18,
  send_weekdays_only: true,
};

/** The in-code seed of a process (the values until the tenant's first PUT). */
export function mahnwesenProcessDefaults(processKey: MahnwesenProcessKeyValue): PutMahnwesenProcessConfigBody {
  const seed = processKey === MAHNWESEN_PROCESS_KEY.P05 ? P05_MAHNWESEN_DEFAULTS : P04_MAHNWESEN_DEFAULTS;
  return { ...seed, frist_days: [...seed.frist_days] };
}

/**
 * Pure: is `now` inside the tenant's Versandfenster (local wall clock in MAHNWESEN_TZ)? The window
 * is [start, end) on the hour; `send_weekdays_only` excludes Sat/Sun. Public holidays are not excluded yet.
 */
export function isWithinSendWindow(
  now: Date,
  cfg: Pick<
    MahnwesenProcessConfigDto,
    'send_window_start_hour' | 'send_window_end_hour' | 'send_weekdays_only'
  >,
): boolean {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: MAHNWESEN_TZ,
    hour: '2-digit',
    hour12: false,
    weekday: 'short',
  }).formatToParts(now);
  const hourStr = parts.find((p) => p.type === 'hour')?.value ?? '0';
  const weekday = parts.find((p) => p.type === 'weekday')?.value ?? '';
  let hour = parseInt(hourStr, 10);
  if (hour === 24) hour = 0; // en-GB hour12:false can emit '24' at midnight
  const isWeekend = weekday === 'Sat' || weekday === 'Sun';
  if (cfg.send_weekdays_only && isWeekend) return false;
  return hour >= cfg.send_window_start_hour && hour < cfg.send_window_end_hour;
}

/** In-process domain events carried on the activity row (placement idiom; no Kafka). Kap. 12. */
export const DUNNING_EVENTS = {
  REMINDER_SENT: 'dunning.reminder_sent',
  REMINDER_FAILED: 'dunning.reminder_failed',
  ESCALATED: 'dunning.escalated',
  BADGE_UPDATED: 'customer.mandate_badge_updated',
  DEADLINE_EXTENDED: 'dunning.deadline_extended',
} as const;
export type DunningEventValue = (typeof DUNNING_EVENTS)[keyof typeof DUNNING_EVENTS];

// ════════════════════════════════════════════════════════════════════════════
// P4-S8: Prozessabschluss (step 27) + the process-state read surface
// ════════════════════════════════════════════════════════════════════════════

/** Kap. 12 events carried on the activity row (no bus; F4/S2 convention). */
export const MANDATE_PROCESS_EVENTS = {
  COMPLETED: 'mandate_process.completed',
  REOPENED: 'mandate_process.reopened',
} as const;

export interface MandateProcessCompletedEventMeta {
  event: 'mandate_process.completed' | 'mandate_process.reopened';
  mandate_id: string;
  /** S1 AM-yyyy-nnnnnn; null for pre-S1 mandates. */
  process_number: string | null;
  kind: 'analyse' | 'broker';
  /** Live F3 rows at 'neu' (S7 A4 «nicht benachrichtigt», incl. late ones) at close; 0 on a clean close. */
  open_relationship_count: number;
  /** S7 rows in OPEN_NOTIFICATION_STATUSES at close; 0 on a clean close. */
  open_notification_count: number;
  /** The reason TEXT lives on the row, never in meta (rule #6). */
  reason_given: boolean;
  reopen_count: number;
  /** The consumer named in Kap. 12: the Analysephase, or the laufende Betreuung after a Brokermandat. */
  handoff: 'analysephase' | 'laufende_betreuung';
  /** Brokermandat: its rows not yet decided at close. */
  open?: number;
  /** Brokermandat: its scope insurers without any row at close. */
  unreported?: number;
  /** Brokermandat: its scope policies without insurer UID at close (one open item «Versicherer-UID fehlt»). */
  missing_carrier_uid?: number;
}

/** A scope insurer of a Brokermandat without any registration row (an open item of its close). */
export interface UnreportedCarrierDto {
  /** The normalised UID, or `name:<name>` for an insurer without UID. */
  carrier_key: string;
  carrier_uid: string | null;
  carrier_name: string | null;
}

/** The DERIVED P4 process status (§0.1): no mandate_status value, computed from the mandate row. */
export type MandateProcessStatus = 'offen' | 'gueltig' | 'abgeschlossen';

/** GET /mandates/:id/process-state. */
export interface MandateProcessStateDto {
  mandate_id: string;
  process_number: string | null;
  status: MandateProcessStatus;
  closed_at: string | null;
  closed_by_app_user_id: string | null;
  /** The closer's display name, for the card. */
  closed_by_name: string | null;
  close_reason: string | null;
  reopened_at: string | null;
  reopen_count: number;
  analysephase_handoff_at: string | null;
  /** Live F3 rows of the subject with status = 'neu' (never notified; S7 A4). */
  open_relationship_count: number;
  /** S7 mandate_insurer_notification rows with status IN OPEN_NOTIFICATION_STATUSES. */
  open_notification_count: number;
  /** Live rows with late_for_mandate_id = mandate AND status = 'neu' (the «n neu» badge). */
  late_relationship_count: number;
  /** status === 'gueltig'. */
  can_close: boolean;
  /** can_close AND (open_relationship_count + open_notification_count + unreported_carrier_count) > 0. */
  requires_reason: boolean;
  /** The mandate's kind (the Brokermandat counts its rows and its unreported scope insurers). */
  kind?: 'analyse' | 'broker';
  /** Brokermandat: its scope insurers without any registration row. */
  unreported_carrier_count?: number;
  unreported_carriers?: UnreportedCarrierDto[];
  /** Brokermandat: its scope policies without insurer UID (together one open item «Versicherer-UID fehlt»). */
  missing_carrier_uid_count?: number;
}

/** POST /mandates/:id/process-close body: reason is 1..1000 chars when present. */
export interface CloseMandateProcessBody {
  reason?: string;
}

/**
 * The Mandatskachel words per badge state (Kap. 7.6/10.5): the badge labels of the api and the
 * mandate chip of the Versicherer-Benachrichtigung read them, so the two never drift.
 */
export const MANDATE_BADGE_STATE_LABEL: Record<MandateBadgeDto['state'], string> = {
  nicht_erteilt: 'nicht erteilt',
  in_verarbeitung: 'in Verarbeitung',
  unterzeichnet: 'unterzeichnet',
  gekuendigt: 'gekündigt',
  beendet: 'beendet',
};

/** The Mandatskachel state rendered from the board row (Kap. 10.5). */
export const MANDATE_TILE_STATE = {
  NICHT_ERTEILT: 'nicht_erteilt',
  IN_VERARBEITUNG: 'in_verarbeitung',
  UNTERZEICHNET: 'unterzeichnet',
  ENTFAELLT: 'entfaellt',
} as const;
export type MandateTileStateValue = (typeof MANDATE_TILE_STATE)[keyof typeof MANDATE_TILE_STATE];

/** German label for a mandate kind (tile + reminder subjects). */
export function mandateKindTileLabel(kind: 'analyse' | 'broker'): string {
  return kind === 'broker' ? 'Brokermandat' : 'Analysemandat';
}

/**
 * Pure (rule #2): compute the Mandatskachel {tileState, tileLabel} from a board row. Used by the API
 * (versandboard) and mirrored by the web tile. `mahnStage` drives the "Erinnerung n" suffix; an
 * `ueberfaellig` vorgang adds " · eskaliert".
 */
export function computeMandateTile(input: {
  vorgangStatus: string;
  mahnStage: number;
  mandateKind: 'analyse' | 'broker' | null;
}): { tileState: MandateTileStateValue; tileLabel: string } {
  const kindLabel = mandateKindTileLabel(input.mandateKind ?? 'analyse');
  switch (input.vorgangStatus) {
    case 'versandt':
    case 'erinnert':
    case 'eingegangen':
    case 'ueberfaellig': {
      let label = `${kindLabel} in Verarbeitung`;
      if (input.vorgangStatus === 'erinnert' && input.mahnStage > 0) {
        label += ` · Erinnerung ${input.mahnStage}`;
      } else if (input.vorgangStatus === 'ueberfaellig') {
        if (input.mahnStage > 0) label += ` · Erinnerung ${input.mahnStage}`;
        label += ' · eskaliert';
      } else if (input.vorgangStatus === 'eingegangen') {
        label += ' · Ruecklauf in Pruefung';
      }
      return { tileState: MANDATE_TILE_STATE.IN_VERARBEITUNG, tileLabel: label };
    }
    case 'abgeschlossen':
      return { tileState: MANDATE_TILE_STATE.UNTERZEICHNET, tileLabel: `${kindLabel} unterzeichnet` };
    case 'abgebrochen':
      return { tileState: MANDATE_TILE_STATE.ENTFAELLT, tileLabel: '' };
    default:
      return { tileState: MANDATE_TILE_STATE.NICHT_ERTEILT, tileLabel: '' };
  }
}
